import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './website-approvals.api';

import type { WebsiteApprovalEntityType, WebsiteApprovalsQuery } from '@utazzo/shared';

const QK = {
  list: (query: WebsiteApprovalsQuery) => ['website-approvals', 'list', query] as const,
  history: (type: WebsiteApprovalEntityType, id: string) =>
    ['website-approvals', 'history', type, id] as const,
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  return null;
};

export const useWebsiteApprovals = (query: WebsiteApprovalsQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listWebsiteApprovals(query),
    staleTime: 30_000,
  });

/**
 * Count for the sidebar "Content approvals" badge, mirroring the product- and
 * lead-approvals badges beside it.
 *
 * Nested under the same `['website-approvals']` root the page and card use, so
 * the approve / reject mutations - which already invalidate that root - refresh
 * this badge too. `pageSize: 1` because only `pagination.totalItems` is wanted;
 * the server counts the whole merged queue before slicing. The 60s refetch
 * matches the neighbouring badges so the counts never drift visibly apart.
 */
export const useWebsiteApprovalsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: ['website-approvals', 'pending-count'] as const,
    queryFn: () => api.listWebsiteApprovals({ type: 'ALL', page: 1, pageSize: 1 }),
    enabled,
    refetchInterval: 60_000,
    // A badge is decoration - a failing count must never surface an error
    // boundary or a toast, it just stays at its last value.
    retry: false,
    throwOnError: false,
  });

export const useWebsiteApprovalHistory = (
  type: WebsiteApprovalEntityType | undefined,
  id: string | undefined,
) =>
  useQuery({
    queryKey:
      type && id ? QK.history(type, id) : (['website-approvals', 'history', 'none'] as const),
    queryFn: () => api.getWebsiteApprovalHistory(type!, id!),
    enabled: Boolean(type && id),
    staleTime: 30_000,
  });

/**
 * A decision changes the record's publish status, so it invalidates the
 * website-health aggregate (tile counts, needs-attention) as well as the
 * queue itself.
 */
const useDecisionMutation = (
  decide: (type: WebsiteApprovalEntityType, id: string, note?: string) => Promise<unknown>,
  successMessage: string,
  failureMessage: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      id,
      note,
    }: {
      type: WebsiteApprovalEntityType;
      id: string;
      note?: string;
    }) => decide(type, id, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-approvals'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'website-health'] });
      toast.success(successMessage);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? failureMessage),
  });
};

export const useApproveWebsiteItem = () =>
  useDecisionMutation(api.approveWebsiteItem, 'Approved and published', 'Failed to approve');

export const useRejectWebsiteItem = () =>
  useDecisionMutation(api.rejectWebsiteItem, 'Sent back to draft', 'Failed to reject');

export const useSubmitForReview = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, id }: { type: WebsiteApprovalEntityType; id: string }) =>
      api.submitForReview(type, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-approvals'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'website-health'] });
      toast.success('Sent to the website manager for review');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to submit for review'),
  });
};
